<template>
  <div class="characters-shell">
    <div class="roster-panel">
      <UiSectionHeader>
        <template #title>Your Roster</template>
        <template #actions>
          <UiChip v-if="rosterFreshness" :variant="rosterFreshness.variant" class="stat-mono num">
            {{ rosterFreshness.label }}
          </UiChip>
          <UiButton size="sm" variant="secondary" @click="openWizard">
            Create Character
          </UiButton>
        </template>
      </UiSectionHeader>

      <div v-if="session.characters.length === 0" class="empty-roster">
        No characters yet. Forge your first hero.
      </div>

      <div class="roster-grid">
        <UiCard
          v-for="c in session.characters"
          :key="c.id"
          class="roster-card"
          :class="{ active: c.id === session.character?.id }"
          role="button"
          @click="select(c.id)"
        >
          <div class="roster-card__header">
            <div>
              <h3>{{ c.name }}</h3>
              <p class="text-muted">{{ c.classId }} • {{ c.areaId }}</p>
            </div>
            <UiChip variant="muted">Lv <span class="stat-mono num">{{ formatNumber(characterLevel(c)) }}</span></UiChip>
          </div>
          <div class="roster-card__stats">
            <span>{{ ICONS.stats.hp }} <span class="stat-mono num">{{ formatHpWithTemp(c.hp, c.temp_hp) }}</span></span>
            <span>{{ ICONS.itemsByType.currency }} <span class="stat-mono num">{{ formatNumber(c.gold) }}</span></span>
          </div>
          <div class="roster-card__actions">
            <UiButton
              size="sm"
              variant="danger"
              @click.stop="confirmDelete(c.id, c.name)"
              :disabled="c.id === session.character?.id"
            >
              Delete
            </UiButton>
            <UiButton size="sm" @click.stop="goArea(c.id)">Enter the World</UiButton>
          </div>
        </UiCard>
      </div>

      <div class="quick-play">
        <UiButton variant="ghost" @click="handleQuickPlay">
          <template #icon>{{ ICONS.actions.dice }}</template>
          Quick Play
        </UiButton>
        <span class="text-muted">Instant action queueing.</span>
      </div>
    </div>

    <UiPanel class="creation-panel" raised>
      <UiSectionHeader>
        <template #title>Create Character</template>
      </UiSectionHeader>
      <div class="creation-form">
        <label class="creation-field">
          <span>Name</span>
          <input v-model="name" class="ui-input" placeholder="Aldric the Bold" />
        </label>
        <label class="creation-field">
          <span>Class</span>
          <select v-model="classId" class="ui-select">
            <option value="fighter">Fighter</option>
            <option value="ranger">Ranger</option>
            <option value="wizard">Wizard</option>
            <option value="rogue">Rogue</option>
            <option value="cleric">Cleric</option>
          </select>
        </label>
        <StatRoller v-model="attributes" />
        <div class="creation-proficiencies">
          <div class="proficiency-group">
            <div class="group-title">Armor Proficiencies</div>
            <div class="pill-grid">
              <label v-for="option in armorOptions" :key="option.value" class="pill">
                <input v-model="armorProficiencies" type="checkbox" :value="option.value" :disabled="armorOptionDisabled(option.value)" />
                <span>{{ option.label }}</span>
              </label>
            </div>
          </div>
          <div class="proficiency-group">
            <div class="group-title">Weapon Proficiencies</div>
            <div class="pill-grid">
              <label v-for="option in weaponOptions" :key="option.value" class="pill">
                <input v-model="weaponProficiencies" type="checkbox" :value="option.value" :disabled="weaponOptionDisabled(option.value)" />
                <span>{{ option.label }}</span>
              </label>
            </div>
          </div>
          <div class="text-muted hint">
            Pick up to {{ MAX_TOTAL_PROFICIENCIES }} total proficiencies ({{ selectedProficiencyCount }}/{{ MAX_TOTAL_PROFICIENCIES }} selected).
            Non-proficient armor hurts initiative. Non-proficient weapons roll attacks at disadvantage.
          </div>
        </div>
        <div class="creation-actions">
          <UiButton @click="create" :disabled="session.loading || !session.isAuthed">
            Forge Hero
          </UiButton>
          <span v-if="session.error" class="error-text">{{ session.error }}</span>
        </div>
      </div>
    </UiPanel>

    <details class="creation-fold">
      <summary>Create a new character</summary>
      <UiPanel class="creation-panel creation-panel--fold" raised>
        <div class="creation-form">
          <label class="creation-field">
            <span>Name</span>
            <input v-model="name" class="ui-input" placeholder="Aldric the Bold" />
          </label>
          <label class="creation-field">
            <span>Class</span>
            <select v-model="classId" class="ui-select">
              <option value="fighter">Fighter</option>
              <option value="ranger">Ranger</option>
              <option value="wizard">Wizard</option>
              <option value="rogue">Rogue</option>
              <option value="cleric">Cleric</option>
            </select>
          </label>
          <StatRoller v-model="attributes" />
          <div class="creation-proficiencies">
            <div class="proficiency-group">
              <div class="group-title">Armor Proficiencies</div>
              <div class="pill-grid">
                <label v-for="option in armorOptions" :key="option.value" class="pill">
                  <input v-model="armorProficiencies" type="checkbox" :value="option.value" :disabled="armorOptionDisabled(option.value)" />
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>
            <div class="proficiency-group">
              <div class="group-title">Weapon Proficiencies</div>
              <div class="pill-grid">
                <label v-for="option in weaponOptions" :key="option.value" class="pill">
                  <input v-model="weaponProficiencies" type="checkbox" :value="option.value" :disabled="weaponOptionDisabled(option.value)" />
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>
            <div class="text-muted hint">
              Pick up to {{ MAX_TOTAL_PROFICIENCIES }} total proficiencies ({{ selectedProficiencyCount }}/{{ MAX_TOTAL_PROFICIENCIES }} selected).
              Non-proficient armor hurts initiative. Non-proficient weapons roll attacks at disadvantage.
            </div>
          </div>
          <div class="creation-actions">
            <UiButton @click="create" :disabled="session.loading || !session.isAuthed">
              Forge Hero
            </UiButton>
            <span v-if="session.error" class="error-text">{{ session.error }}</span>
          </div>
        </div>
      </UiPanel>
    </details>

    <div v-if="wizardOpen" class="wizard-scrim" @click.self="closeWizard">
      <UiPanel class="character-wizard" raised>
        <div class="wizard-header">
          <h2 class="wizard-title">{{ wizardTitle }}</h2>
          <UiButton size="sm" variant="ghost" @click="closeWizard">Close</UiButton>
        </div>

        <div v-if="wizardStep === 0" class="wizard-step stack">
          <label class="creation-field">
            <span>Name</span>
            <input v-model="name" class="ui-input" placeholder="Aldric the Bold" />
          </label>
          <label class="creation-field">
            <span>Class</span>
            <select v-model="classId" class="ui-select">
              <option value="fighter">Fighter</option>
              <option value="ranger">Ranger</option>
              <option value="wizard">Wizard</option>
              <option value="rogue">Rogue</option>
              <option value="cleric">Cleric</option>
            </select>
          </label>
        </div>

        <div v-else-if="wizardStep === 1" class="wizard-step stack">
          <h3>Stats</h3>
          <StatRoller v-model="attributes" />
        </div>

        <div v-else-if="wizardStep === 2" class="wizard-step stack">
          <h3>Proficiencies</h3>
          <div class="creation-proficiencies">
            <div class="proficiency-group">
              <div class="group-title">Armor Proficiencies</div>
              <div class="pill-grid">
                <label v-for="option in armorOptions" :key="option.value" class="pill">
                  <input v-model="armorProficiencies" type="checkbox" :value="option.value" :disabled="armorOptionDisabled(option.value)" />
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>
            <div class="proficiency-group">
              <div class="group-title">Weapon Proficiencies</div>
              <div class="pill-grid">
                <label v-for="option in weaponOptions" :key="option.value" class="pill">
                  <input v-model="weaponProficiencies" type="checkbox" :value="option.value" :disabled="weaponOptionDisabled(option.value)" />
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>
            <div class="text-muted hint">
              Pick up to {{ MAX_TOTAL_PROFICIENCIES }} total proficiencies ({{ selectedProficiencyCount }}/{{ MAX_TOTAL_PROFICIENCIES }} selected).
              Non-proficient armor hurts initiative. Non-proficient weapons roll attacks at disadvantage.
            </div>
          </div>
        </div>

        <div v-else-if="wizardStep === 3" class="wizard-step stack">
          <h3>Starting Loadout</h3>
          <div class="text-muted">
            Your starting gear and proficiencies will be based on your class and selected proficiencies.
          </div>
        </div>

        <div v-else class="wizard-step stack">
          <h3>Confirm</h3>
          <div class="text-muted">
            You can adjust your loadout later, but proficiencies are fixed for now.
          </div>
          <div class="creation-actions">
            <UiButton @click="create" :disabled="session.loading || !session.isAuthed">
              Forge Hero
            </UiButton>
            <span v-if="session.error" class="error-text">{{ session.error }}</span>
          </div>
        </div>

        <div class="wizard-actions">
          <UiButton size="sm" variant="ghost" @click="prevWizard" :disabled="wizardStep === 0">
            Back
          </UiButton>
          <UiButton size="sm" @click="nextWizard" v-if="wizardStep < 4">
            Next
          </UiButton>
        </div>
      </UiPanel>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import StatRoller from "../../../components/StatRoller.vue";
import type { ArmorCategory, Attributes, ClassId, Character, WeaponProficiency } from "@idle-rpg/shared";
import { useSessionStore } from "../../../store/session";
import { useToast } from "../../../composables/useToast";
import { useConfirm } from "../../../composables/useConfirm";
import { ICONS } from "../../../lib/icons";
import { formatHpWithTemp, formatNumber } from "../../../lib/format";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCard from "../../../components/ui/UiCard.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiChip from "../../../components/ui/UiChip.vue";

const session = useSessionStore();
const router = useRouter();
const { addToast } = useToast();
const { confirm } = useConfirm();
const lastUpdatedAt = ref<number | null>(null);
const ROSTER_REFRESH_MS = 15000;

const name = ref("");
const classId = ref<ClassId>("fighter");
const attributes = ref<Attributes>({
  str: 10,
  dex: 10,
  con: 10,
  int: 10,
  wis: 10,
  cha: 10
});
const armorProficiencies = ref<ArmorCategory[]>([]);
const weaponProficiencies = ref<WeaponProficiency[]>([]);

const wizardOpen = ref(false);
const wizardStep = ref(0);

const wizardTitle = computed(() => "Create Character");

const MAX_TOTAL_PROFICIENCIES = 3;
const selectedProficiencyCount = computed(
  () => armorProficiencies.value.length + weaponProficiencies.value.length
);

function armorOptionDisabled(value: ArmorCategory): boolean {
  if (armorProficiencies.value.includes(value)) return false;
  return selectedProficiencyCount.value >= MAX_TOTAL_PROFICIENCIES;
}

function weaponOptionDisabled(value: WeaponProficiency): boolean {
  if (weaponProficiencies.value.includes(value)) return false;
  return selectedProficiencyCount.value >= MAX_TOTAL_PROFICIENCIES;
}

const armorOptions: Array<{ value: ArmorCategory; label: string }> = [
  { value: "heavy", label: "Heavy" },
  { value: "light", label: "Light" },
  { value: "clothing", label: "Clothing" },
  { value: "shield", label: "Shield" }
];

const weaponOptions: Array<{ value: WeaponProficiency; label: string }> = [
  { value: "melee", label: "Melee" },
  { value: "ranged", label: "Ranged" },
  { value: "magic", label: "Magic" }
];

function defaultsForClass(id: ClassId): { armor: ArmorCategory[]; weapon: WeaponProficiency[] } {
  switch (id) {
    case "wizard":
      return { armor: ["clothing"], weapon: ["magic"] };
    case "cleric":
      return { armor: ["light", "shield"], weapon: ["magic"] };
    case "ranger":
      return { armor: ["light"], weapon: ["melee", "ranged"] };
    case "rogue":
      return { armor: ["light"], weapon: ["melee", "ranged"] };
    case "fighter":
    default:
      return { armor: ["heavy", "shield"], weapon: ["melee"] };
  }
}

watch(
  classId,
  (next) => {
    const defaults = defaultsForClass(next);
    armorProficiencies.value = [...defaults.armor];
    weaponProficiencies.value = [...defaults.weapon];
  },
  { immediate: true }
);


async function refresh(background = false) {
  if (!session.isAuthed) return;
  if (background && session.backgroundLoading) return;
  if (!background && session.loading) return;
  await session.loadCharacters({ background });
  lastUpdatedAt.value = Date.now();
}

async function create() {
  await session.createCharacter({
    name: name.value,
    classId: classId.value,
    attributes: attributes.value,
    armorProficiencies: armorProficiencies.value,
    weaponProficiencies: weaponProficiencies.value
  });
  if (session.character) router.push("/game");
}

function openWizard() {
  wizardStep.value = 0;
  wizardOpen.value = true;
}

function closeWizard() {
  wizardOpen.value = false;
}

function nextWizard() {
  wizardStep.value = Math.min(4, wizardStep.value + 1);
}

function prevWizard() {
  wizardStep.value = Math.max(0, wizardStep.value - 1);
}

function select(id: string) {
  session.selectCharacter(id);
}

function goArea(id: string) {
  session.selectCharacter(id);
  router.push("/game");
}

async function confirmDelete(id: string, characterName: string) {
  const ok = await confirm({
    title: "Delete Character",
    subtitle: characterName,
    body: `Are you sure you want to delete ${characterName}? This cannot be undone.`,
    confirmLabel: "Delete",
    cancelLabel: "Cancel",
    variant: "danger"
  });
  if (!ok) return;
  await session.deleteCharacter(id);
}

function handleQuickPlay() {
  if (!session.isAuthed || session.loading) return;
  const classes: ClassId[] = ["fighter", "ranger", "wizard", "rogue", "cleric"];
  const idx = Math.floor(Math.random() * classes.length);
  const defaults = defaultsForClass(classes[idx]);
  const immersiveNames = [
    "Max",
    "Alcor",
    "Lyra",
    "Kael",
    "Voss",
    "Seren",
    "Juno",
    "Thane",
    "Mira",
    "Orin",
    "Nyx",
    "Cass",
    "Vale",
    "Rune",
    "Arden"
  ];
  const baseName = immersiveNames[Math.floor(Math.random() * immersiveNames.length)];
  const suffix = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
  const quickName = `${baseName}-${suffix}`;
  void session
    .createCharacter({
      name: quickName,
      classId: classes[idx],
      attributes: attributes.value,
      armorProficiencies: defaults.armor,
      weaponProficiencies: defaults.weapon
    })
    .then(() => {
      if (session.character) {
        router.push("/game");
        addToast(`Welcome, ${session.character.name}.`, "success");
      }
    });
}

function characterLevel(character: Character): number | null {
  return character.skillProgress.find((skill) => skill.id === "character")?.level ?? null;
}

onMounted(() => {
  if (!session.isAuthed) {
    router.push("/");
  } else {
    refresh();
  }

  handleVisibilityChange();
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});

const rosterFreshness = computed(() => {
  if (session.loading || session.backgroundLoading) return { variant: "muted" as const, label: "Syncing" };
  if (lastUpdatedAt.value) {
    const stale = Date.now() - lastUpdatedAt.value > ROSTER_REFRESH_MS * 2 + 1000;
    if (stale) return { variant: "warning" as const, label: "Stale" };
  }
  return { variant: "muted" as const, label: "Synced" };
});

function handleVisibilityChange() {
  if (!document.hidden && session.isAuthed) {
    void refresh(true);
  }
}
</script>

<style scoped>
.characters-shell {
  display: grid;
  grid-template-columns: minmax(280px, 1.4fr) minmax(320px, 420px);
  gap: 1.5rem;
  padding: clamp(1.5rem, 4vw, 3rem);
  min-height: 100vh;
}

.roster-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
}

.roster-grid {
  display: grid;
  gap: 1rem;
  max-height: calc(100vh - 320px);
  overflow: auto;
  padding-right: 0.35rem;
}

.roster-card {
  transition: border-color var(--duration-fast) var(--ease-out);
  cursor: pointer;
}

.roster-card.active {
  border-color: color-mix(in srgb, var(--accent) 60%, var(--color-border-strong));
  box-shadow: var(--shadow-accent);
}

.roster-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.roster-card__header h3 {
  margin: 0 0 0.2rem;
}

.roster-card__stats {
  display: flex;
  gap: 1rem;
  margin-top: 0.8rem;
  color: var(--color-muted);
}

.roster-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1rem;
  justify-content: flex-end;
}

.empty-roster {
  padding: 1rem;
  border-radius: var(--radius-md);
  border: 1px dashed var(--color-border);
  color: var(--color-muted);
}

.quick-play {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 0.5rem;
}

.creation-panel {
  align-self: start;
  position: sticky;
  top: 1.5rem;
}

.creation-form {
  display: grid;
  gap: 1rem;
}

.creation-field {
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
}

.creation-field input,
.creation-field select {
  width: 100%;
}

.creation-proficiencies {
  display: grid;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--glass-1);
}

.proficiency-group {
  display: grid;
  gap: 0.5rem;
}

.group-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-muted);
}

.pill-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.5rem;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.6rem;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: var(--glass-0);
  font-size: 0.85rem;
  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out);
}

.pill input {
  accent-color: var(--accent);
}

.pill:hover {
  border-color: color-mix(in srgb, var(--accent) 60%, var(--color-border));
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--accent) 40%, transparent);
}

.hint {
  font-size: 0.8rem;
}

.creation-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.error-text {
  color: var(--color-danger);
  font-size: 0.85rem;
}

.creation-fold {
  display: none;
}

.wizard-scrim {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: color-mix(in srgb, var(--color-bg) 70%, transparent);
}

.character-wizard {
  width: min(720px, 100%);
  max-height: min(820px, 100%);
  overflow: auto;
}

.wizard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.wizard-title {
  margin: 0;
  font-family: var(--font-display);
}

.wizard-actions {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.creation-fold summary {
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

@media (max-width: 1000px) {
  .characters-shell {
    grid-template-columns: 1fr;
  }

  .creation-panel {
    display: none;
  }

  .creation-panel--fold {
    display: block;
    position: static;
  }

  .creation-fold {
    display: block;
  }
}
</style>
